import { ReactNode } from 'react'
import { useRouter } from 'next/compat/router'
import Image from 'next/legacy/image'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import {
  Box,
  Button as DSButton,
  IconArrowUpRight,
  IconLockOutline,
  useDialogController
} from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'

import { colors } from '@/styles'
import { isEmptyOrNull } from '@/utils'
import { blurImage } from '@/utils/image'
import { getUrl } from '@/utils/listings'
import { toUnit } from '@/utils/styles'
import { useEnsureLoggedIn } from '@/hooks/use-ensure-loggedIn'
import useSessionComplete from '@/hooks/use-session-complete'
import DSLoginModal from '@/features/Auth/login-modal/ds-login-modal'
import FavouriteButton from '@/components/favourite-button'
import { PillWrapper, VerifiedPill } from '@/components/pill'
import Skeleton from '@/components/skeleton'
import { Label } from '@/components/text'

import BlurredText from '../blurred-text'
import { useTermsDialogContext } from '../terms-dialog/context'
import { ListingCardActions } from './actions'
import type { Listing } from './types'
import {
  getIsRented,
  getListingCardDlv,
  setListingCardDataLayer
} from './utils'

const Outer = styled.div<{
  height: number
  $isRow: boolean
  borderRadius: number
  $loading?: boolean
}>`
  display: flex;
  flex-direction: ${props => (props.$isRow ? 'row' : 'column')};
  width: 100%;
  text-decoration: none;
  box-shadow: 0px 0px 0px 1px ${colors.grey};
  border-radius: ${props => toUnit(props.borderRadius)};

  ${props => props.height && `height: ${props.height}px;`}

  ${props => props.$loading && `pointer-events: none;`}
`

const Bottom = styled.div<{
  height: number
  isRow: boolean
  borderRadius: number
}>`
  width: 100%;
  border-bottom-left-radius: ${props =>
    !props.isRow && toUnit(props.borderRadius)};
  border-top-right-radius: ${props =>
    props.isRow && toUnit(props.borderRadius)};
  border-bottom-right-radius: ${props => toUnit(props.borderRadius)};
  ${props => props.isRow && `overflow: scroll`}
  height: ${props => props.height}px;
  background: ${colors.white};
  border-top: none;
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 16px;
`

const Wrapper = styled.div<{
  isRow: boolean
  borderRadius: number
  height: number
}>`
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0);
    transition: background-color 0.2s ease-in-out;
    border-top-left-radius: ${props => toUnit(props.borderRadius)};
    border-bottom-left-radius: ${props =>
      props.isRow && toUnit(props.borderRadius)};
    border-top-right-radius: ${props =>
      !props.isRow && toUnit(props.borderRadius)};
  }

  width: 100%;
  border-top-left-radius: ${props => toUnit(props.borderRadius)};
  border-bottom-left-radius: ${props =>
    props.isRow && toUnit(props.borderRadius)};
  border-top-right-radius: ${props =>
    !props.isRow && toUnit(props.borderRadius)};
  position: relative;
  height: ${props => props.height}px;
  background: transparent center no-repeat;
  background-size: cover;
  display: flex;
  align-items: flex-end;
  animation: fadeIn ease-in-out 0.2s;
  // Safari border radius overflow bug fix:
  // https://gist.github.com/ayamflow/b602ab436ac9f05660d9c15190f4fd7b
  -webkit-mask-image: -webkit-radial-gradient(white, black);

  @keyframes fadeIn {
    0% {
      opacity: 0.2;
    }
    100% {
      opacity: 1;
    }
  }
`

const Divider = styled.div`
  position: relative;
  height: 1px;
  border-top: 1px solid ${colors.grey88};
`

const Address = styled(Label)`
  color: ${vars.foregroundColor.neutral};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  margin-bottom: 4px;
`

const SecondaryDetails = styled(Address)`
  color: ${vars.foregroundColor.secondary};
  margin: 0;
`

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const DetailText = styled(Label)`
  white-space: nowrap;
  color: ${vars.foregroundColor.secondary};
`

const BlurredDetailText = styled(DetailText)`
  color: transparent;
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
`

const FavouriteButtonStyled = styled(FavouriteButton)`
  z-index: 10;

  svg {
    width: 22px;
    height: 20px;
  }

  :hover {
    svg {
      transition: transform 0.2s;
      transform: scale(1.05);
    }
  }
`

const PrimaryInfoWrapper = styled.div``

const PriceRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 4px;
`

const Ellipsis = styled.div`
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const Amount = styled.span`
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  color: ${vars.foregroundColor.neutral};
`

const defaultInfoHeight = 206

type PropsType = {
  listing: Listing
  featured?: boolean
  height?: number
  infoHeight?: number
  favouriteButton?: boolean
  newTab?: boolean
  loading?: boolean
  customFooter?: ReactNode
  noSchema?: boolean
  noButtons?: boolean
  skeleton?: boolean
  isRow?: boolean
  customBaseUrl?: string
  isLoggedIn?: boolean
  showDSLoginModal?: () => void
  isBuildingModal?: boolean
  showDSMessageModal?: () => void
  setListingData?: (listing: Listing) => void
}

const LockedListingCard = ({
  listing,
  featured = false,
  height = 216,
  infoHeight = defaultInfoHeight,
  favouriteButton = false,
  loading = false,
  customFooter,
  noSchema = false,
  noButtons = false,
  skeleton = false,
  isRow = false,
  customBaseUrl = '',
  isBuildingModal = false
}: PropsType) => {
  const { t } = useTranslation()

  const { showModalForRequiredTerms } = useTermsDialogContext()

  const isLoggedIn = useSessionComplete()
  const {
    visible: loginDSModalVisible,
    showModal: showDSLoginModal,
    closeModal: closeDSLoginModal
  } = useDialogController()

  const ensureLoggedIn = useEnsureLoggedIn({
    showLogin: showDSLoginModal
  })

  const loadingOrSkeleton = loading || skeleton

  const isRented = getIsRented(listing)

  const size = {
    image: height,
    text: isRented || noButtons ? infoHeight - 60 : infoHeight
  }
  const totalSize = isRow ? size.image : size.image + size.text

  const unitImageFiles = listing.unit_files?.filter(
    file => !file.tag?.includes('video')
  )
  const isStreetNumberHidden =
    !!Number(listing.hide_unit_number) && listing.unit_type_txt_id === 'HOUSE'

  // For similar listings, different data is returned
  const cover =
    listing.cover_photo?.aws_s3_key || listing.cover_photo_aws_s3_key
  const images = !isEmptyOrNull(unitImageFiles)
    ? unitImageFiles
    : [{ aws_s3_key: cover }]

  const BORDER_RADIUS = 12

  const router = useRouter()
  const listingLink = customBaseUrl
    ? `${customBaseUrl}${getUrl(listing)}`
    : getUrl(listing)

  const isCustomCompanyPage = router?.pathname?.includes('/ext/company')

  const getBlurredCover = (images: { aws_s3_key?: string; tag?: string }[]) => {
    if (!images.length) return false
    const cover = images.find(({ tag }) => tag === 'cover_photo')
    return cover ? `${cover.aws_s3_key}` : `${images[0].aws_s3_key}`
  }

  const getUnitType = () => {
    if (listing.unit_type_scope_txt_id === 'SINGLE') return 'Room'
    else if (listing.unit_type_txt_id === 'CONDO') return 'ApartmentComplex'
    else if (listing.unit_type_txt_id === 'HOUSE') return 'House'
    else return 'Accommodation'
  }
  const getListingUrl = () => {
    const listingUrl = getUrl({
      building_type: listing.unit_type_txt_id?.toLowerCase(),
      listing_id: listing.listing_id,
      city: listing.city
    })
    return `https://liv.rent${listingUrl}`
  }
  const getListingSchema = () =>
    JSON.stringify(
      isStreetNumberHidden
        ? {
            '@context': 'http://schema.org',
            '@type': getUnitType(),
            name: getListingName(),
            url: getListingUrl(),
            address: {
              '@type': 'PostalAddress',
              addressCountry: 'CA',
              addressLocality: listing.city,
              addressRegion: listing.state,
              name: `${listing.street_name}, ${listing.city}, ${listing.state}`,
              streetAddress: listing.street_name
            },
            containedInPlace: {
              '@type': 'City',
              url: `https://liv.rent/rental-listings/city/${listing.city?.toLowerCase()}`,
              name: listing.city,
              address: {
                '@type': 'PostalAddress',
                addressCountry: 'CA',
                addressLocality: listing.city,
                addressRegion: listing.state
              }
            }
          }
        : {
            '@context': 'http://schema.org',
            '@type': getUnitType(),
            name: getListingName(),
            url: getListingUrl(),
            address: {
              '@type': 'PostalAddress',
              addressCountry: 'CA',
              addressLocality: listing.city,
              addressRegion: listing.state,
              name: `${listing.full_street_name}, ${listing.city}, ${listing.state}`,
              postalCode: listing.zip,
              streetAddress: listing.full_street_name
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: listing.location?.lat,
              longitude: listing.location?.lon
            },
            containedInPlace: {
              '@type': 'City',
              url: `https://liv.rent/rental-listings/city/${listing.city?.toLowerCase()}`,
              name: listing.city,
              address: {
                '@type': 'PostalAddress',
                addressCountry: 'CA',
                addressLocality: listing.city,
                addressRegion: listing.state
              }
            }
          }
    )
  const getListingName = () => {
    const addressText = `for Rent at ${
      isStreetNumberHidden ? listing.street_name : listing.full_street_name
    }, ${listing.city}, ${listing.state}`
    if (listing.unit_type_scope_txt_id === 'SINGLE')
      return `Room ${addressText}`
    else if (listing.unit_type_txt_id === 'CONDO')
      return `Apartments ${addressText}`
    else if (listing.unit_type_txt_id === 'HOUSE') return `House ${addressText}`
    else return `Townhouse ${addressText}`
  }

  const FavoriteButton = () => (
    <>
      {!loadingOrSkeleton && favouriteButton && !isRented && (
        <FavouriteButtonStyled
          title="Add to Favourites"
          aria-label="Add to Favourites"
          listingId={listing.listing_id}
          eventId="listing-card-click-save"
          noShadow
          iconColor={colors.blue16}
          offColor={colors.blue16}
          publicViewing={listing.public_viewing}
          isBuildingListModal={isBuildingModal}
          isCardLocked={true}
          showDSLoginModal={showDSLoginModal}
          listingCardDlv={getListingCardDlv(listing, featured)}
        />
      )}
    </>
  )
  const src = getBlurredCover(images)
  const blurredSrc =
    src &&
    blurImage(src, {
      width: 300,
      filterType: 'blur',
      quality: 30
    })

  return (
    <Box width="full">
      <Outer
        height={totalSize}
        borderRadius={BORDER_RADIUS}
        $loading={loadingOrSkeleton}
        $isRow={isRow}
        role="group"
        aria-label={`Most relevant blurred listing ${
          listing.id || listing.listing_id
        }`}
      >
        <Wrapper
          height={size.image}
          borderRadius={BORDER_RADIUS}
          rel="noopener noreferrer"
          isRow={isRow}
        >
          {blurredSrc && (
            <Image
              blurDataURL={blurredSrc}
              src={blurredSrc}
              alt={'blurred image'}
              sizes="345px"
              layout="fill"
              objectFit="cover"
            />
          )}

          {skeleton ? (
            <Skeleton width="100%" height="100%" showLogo />
          ) : (
            <Box
              width="full"
              height="full"
              position="absolute"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box zIndex="modal" background="customLight">
                {isLoggedIn ? (
                  <DSButton
                    icon={<IconArrowUpRight />}
                    variant="ghost"
                    id="listing_taptoview_click"
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()

                      setListingCardDataLayer({
                        listing,
                        featured,
                        eventId: 'listing_taptoview_click'
                      })
                      ensureLoggedIn(() =>
                        showModalForRequiredTerms('trreb_agreements', () =>
                          window.open(listingLink, '_blank')
                        )
                      )
                    }}
                  >
                    {t('b.auth.view.tap_to_view', 'Tap to view')}
                  </DSButton>
                ) : (
                  <DSButton
                    icon={<IconLockOutline />}
                    variant="ghost"
                    id="listing_logintoview_click"
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()

                      setListingCardDataLayer({
                        listing,
                        featured,
                        eventId: 'listing_logintoview_click'
                      })
                      ensureLoggedIn(() =>
                        showModalForRequiredTerms('trreb_agreements', () =>
                          window.open(listingLink, '_blank')
                        )
                      )
                    }}
                  >
                    {t('b.auth.view.login_to_view', 'Log in to View')}
                  </DSButton>
                )}
              </Box>
            </Box>
          )}
          <PillWrapper>{<VerifiedPill />}</PillWrapper>
        </Wrapper>
        {/* Bottom info section */}
        <Bottom
          height={isRow ? totalSize : size.text}
          borderRadius={BORDER_RADIUS}
          isRow={isRow}
        >
          <PrimaryInfoWrapper>
            {!isCustomCompanyPage && (
              <>
                <PriceRow>
                  <div>
                    <Amount id="listing-card-price">
                      <BlurredText variant={{ size: 'large' }}>
                        $****
                      </BlurredText>
                    </Amount>
                    <BlurredText variant={{ size: 'small', weight: 'medium' }}>
                      {' '}
                      / ****
                    </BlurredText>
                  </div>
                  <FavoriteButton />
                </PriceRow>
                {!featured && (
                  <DetailRow>
                    <BlurredDetailText>* Bed</BlurredDetailText>
                    <BlurredDetailText>·</BlurredDetailText>
                    <BlurredDetailText>* Bath</BlurredDetailText>
                    <BlurredDetailText>·</BlurredDetailText>
                    <BlurredDetailText>* ft²</BlurredDetailText>
                  </DetailRow>
                )}
              </>
            )}
          </PrimaryInfoWrapper>

          {!isRow && (
            <>
              <Divider />

              {/* Address row */}

              <DetailRow>
                <Ellipsis>
                  <Address
                    fontSize="14px"
                    type="large"
                    id="listing-card-address"
                  >
                    <BlurredText variant={{ size: 'small' }}>****</BlurredText>
                  </Address>
                  <SecondaryDetails id="listing-card-type">
                    <BlurredText variant={{ size: 'small' }}>
                      Vancouver · Apartment for Rent
                    </BlurredText>
                  </SecondaryDetails>
                </Ellipsis>
              </DetailRow>
            </>
          )}

          {customFooter}

          {/* Control button row */}
          {!isRented && !noButtons && (
            <ListingCardActions
              listing={listing}
              isCustomCompanyPage={isCustomCompanyPage}
              customBaseUrl={customBaseUrl}
              loading={skeleton}
              isCardLocked
              featured={featured}
            />
          )}
        </Bottom>

        {(!noSchema || skeleton) && (
          <script
            defer
            async
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: getListingSchema()
            }}
          />
        )}
      </Outer>
      <DSLoginModal
        mode={'login'}
        eventId="web-signup-submit-login-redirect-mls"
        visible={loginDSModalVisible}
        onClose={closeDSLoginModal}
        t={t}
        listingId={parseInt(listing.listing_id)}
      />
    </Box>
  )
}

export default LockedListingCard
